import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { API } from "@/config";

const APP_STATUSES = ["new", "reviewed", "shortlisted", "rejected"];

export const JobsSection = () => {
  const [tab, setTab] = useState("postings");
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: "", location: "", type: "Full-time", openings: 1, description: "" });

  const fetchJobs = async () => {
    const { data } = await axios.get(`${API}/jobs/all`, { withCredentials: true });
    setJobs(data);
  };
  const fetchApplications = async () => {
    const { data } = await axios.get(`${API}/applications`, { withCredentials: true });
    setApplications(data);
  };

  useEffect(() => {
    fetchJobs();
    fetchApplications();
  }, []);

  const addJob = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/jobs`, form, { withCredentials: true });
      toast.success("Job posted");
      setShowAdd(false);
      setForm({ title: "", location: "", type: "Full-time", openings: 1, description: "" });
      fetchJobs();
    } catch {
      toast.error("Failed to post job");
    }
  };

  const setJobStatus = async (id, status) => {
    await axios.patch(`${API}/jobs/${id}`, { status }, { withCredentials: true });
    fetchJobs();
  };

  const deleteJob = async (id) => {
    await axios.delete(`${API}/jobs/${id}`, { withCredentials: true });
    toast.success("Job deleted");
    fetchJobs();
  };

  const setAppStatus = async (id, status) => {
    await axios.patch(`${API}/applications/${id}`, { status }, { withCredentials: true });
    toast.success("Status updated");
    fetchApplications();
  };

  return (
    <div data-testid="jobs-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          <Button variant={tab === "postings" ? "default" : "outline"} size="sm" data-testid="tab-postings" onClick={() => setTab("postings")}>
            Job Postings ({jobs.length})
          </Button>
          <Button variant={tab === "applications" ? "default" : "outline"} size="sm" data-testid="tab-applications" onClick={() => setTab("applications")}>
            Applications ({applications.length})
          </Button>
        </div>
        {tab === "postings" && (
          <Button size="sm" className="gap-1" data-testid="add-job-button" onClick={() => setShowAdd(true)}>
            <Plus className="h-4 w-4" /> Post a Job
          </Button>
        )}
      </div>

      {tab === "postings" ? (
        <div className="mt-6 overflow-x-auto rounded-2xl border border-border bg-card shadow-sm">
          <Table data-testid="jobs-table">
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Openings</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map((j) => (
                <TableRow key={j.id} data-testid={`job-row-${j.id}`}>
                  <TableCell className="font-medium">{j.title}</TableCell>
                  <TableCell>{j.location}</TableCell>
                  <TableCell>{j.type}</TableCell>
                  <TableCell>{j.openings}</TableCell>
                  <TableCell>
                    <Select value={j.status} onValueChange={(v) => setJobStatus(j.id, v)}>
                      <SelectTrigger className="w-28" data-testid={`job-status-${j.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">open</SelectItem>
                        <SelectItem value="closed">closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" data-testid={`delete-job-${j.id}`} onClick={() => deleteJob(j.id)} aria-label="Delete job">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-2xl border border-border bg-card shadow-sm">
          <Table data-testid="applications-table">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Applicant</TableHead>
                <TableHead>Position</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Resume</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {applications.map((a) => (
                <TableRow key={a.id} data-testid={`application-row-${a.id}`}>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    {new Date(a.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="font-medium">{a.name}</TableCell>
                  <TableCell>{a.job_title}</TableCell>
                  <TableCell className="text-sm">
                    {a.phone}
                    <span className="block text-xs text-muted-foreground">{a.email}</span>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1"
                      data-testid={`resume-download-${a.id}`}
                      onClick={() => window.open(`${API}/applications/${a.id}/resume`, "_blank")}
                    >
                      <FileDown className="h-3.5 w-3.5" /> Resume
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Select value={a.status} onValueChange={(v) => setAppStatus(a.id, v)}>
                      <SelectTrigger className="w-32" data-testid={`application-status-${a.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {APP_STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-job-dialog" className="max-w-lg" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading">Post a New Job</DialogTitle>
          </DialogHeader>
          <form onSubmit={addJob} className="grid gap-4" data-testid="add-job-form">
            <div className="grid gap-2">
              <Label>Job Title *</Label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="job-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Location *</Label>
                <Input required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="job-location-input" />
              </div>
              <div className="grid gap-2">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="job-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Full-time", "Part-time", "Contract"].map((ty) => (
                      <SelectItem key={ty} value={ty}>{ty}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Openings</Label>
              <Input type="number" min="1" value={form.openings} onChange={(e) => setForm({ ...form, openings: Number(e.target.value) })} data-testid="job-openings-input" />
            </div>
            <div className="grid gap-2">
              <Label>Description</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="job-description-input" />
            </div>
            <Button type="submit" data-testid="job-submit-button">Post Job</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
